import React, {useEffect, useState} from 'react';
import {DropDownNative, Modal, Spinner} from "@tag/tag-components-react-v4";
import {CustomerDTO} from "../types/CustomerDTO";
import CustomerService from "../services/CustomerService";
import AppGroupService from "../services/AppGroupService";
import {AppGroup} from "../types/AppGroup";
import {useMsal} from "@azure/msal-react";
import HelperMethods from "../../General/services/HelperMethods";
import { ProjectAccents } from "../../General/config/projectAccents";

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

function MoveCustomerToAppGroupModal(props: { visible: boolean, selectedCustomer: CustomerDTO, handleClose: Function, handleCustomerMenuModalClose: Function, setLoading: Function, setAlert: Function, reloadCustomerList: Function }) {
    
    const [appGroups, setAppGroups] = useState<AppGroup[]>([]);
    const [loading, setLoading] = useState<boolean>();
    const [selectedAppGroupServerId, setSelectedAppGroupServerId] = useState<number>();
    const [selectedCustomersAppGroupDbVersion, setSelectedCustomersAppGroupDbVersion] = useState<string>();
    const [selectedAppGroupDbVersion, setSelectedAppGroupDbVersion] = useState<string>();

    const { accounts, inProgress } = useMsal();
    
    useEffect(() => {
        setLoading(true)
        AppGroupService.getAppGroups(props.selectedCustomer.environmentName).then((resp) => {
            setAppGroups(resp);
            setLoading(false)
        });


        AppGroupService.getSqlServerVersion(props.selectedCustomer.appgrpServerId, props.selectedCustomer.environmentName).then((resp) => {
            setSelectedCustomersAppGroupDbVersion(resp)
        })
    }, [props.selectedCustomer.appgrpServerId, props.selectedCustomer.environmentName]);
    
    useEffect(() => {
        setLoading(true)
        if (selectedAppGroupServerId) {
            AppGroupService.getSqlServerVersion(selectedAppGroupServerId, props.selectedCustomer.environmentName).then((resp) => {
                setSelectedAppGroupDbVersion(resp)
                setLoading(false)
            })
        }
        else {
            setSelectedAppGroupDbVersion(undefined)
            setLoading(false)
        }
    }, [selectedAppGroupServerId, props.selectedCustomer.environmentName]);
    
    const isMoveValid = () => {
        if (!selectedCustomersAppGroupDbVersion || !selectedAppGroupDbVersion) {
            return false;
        }
        
        return HelperMethods.isSqlServerVersionGreaterOrEqual(selectedAppGroupDbVersion, selectedCustomersAppGroupDbVersion);
    }
    
    const renderAppGroupsDropdown = () => {
        const data = [{ label: 'Select an AppGroup', value: 0 }];
        
        appGroups.forEach((ag: AppGroup) => {
            data.push(
                {label: ag.name, value: ag.serverId}
            )
        });

        return (
            <DropDownNative
                name='template'
                value={selectedAppGroupServerId}
                data={data}
                onChange={(e) => {setSelectedAppGroupServerId(e.target.value)}}
            />
        )
    }
    
    const handleMoveCustomer = async () => {
        if (!selectedAppGroupServerId) {
            throw new Error();
        }
        
        const foundAppGrp = appGroups.find(n => n.serverId === selectedAppGroupServerId);
        if (!foundAppGrp) {
            throw new Error();
        }
        
        if (!isMoveValid()) {
            throw new Error();
        }
        
        props.setLoading('Starting move...');
        
        try {
            const response = await CustomerService.moveCustomer(props.selectedCustomer.clientId, foundAppGrp.name, props.selectedCustomer.environmentName);
            const taskId = response.taskId;

            let status;
            do {
                await delay(2000);
                status = await CustomerService.getMoveCustomerStatus(taskId);
                props.setLoading(status.progressMessage || 'Moving...');
            } while (!status.complete && !status.errorMessage);
            
            if (status.errorMessage) {
                props.setAlert({type: 'error', message: status.errorMessage});
            } else {
                props.setAlert({type: 'success', message: 'Customer move to app group success'});
                props.reloadCustomerList();
            }
        } catch (e) {
            props.setAlert({type: 'error', message: 'An error has occurred, please contact HSC Care DevOps'});
        } finally {
            props.setLoading(false);
            props.handleClose();
            props.handleCustomerMenuModalClose();
        }
    }
    
    const renderModalContent = () => {
        if (loading) {
            return <Spinner />;
        }
        return (
            <>
                {renderAppGroupsDropdown()}
                {selectedAppGroupServerId ?
                    <>
                        <p>
                            Source DB Version - {selectedCustomersAppGroupDbVersion}
                            <br />
                            Target DB Version - {selectedAppGroupDbVersion}
                        </p>
                        { !isMoveValid() ?
                           <div style={{color: 'red'}}>
                               Move is not valid - Target DB must be a newer or same version
                           </div>: undefined
                        }
                    </> : undefined
                }
            </>
        )
    }
    
    let content;
    content = <>
        <Modal
            visible={props.visible}
            accent={ProjectAccents.PeoplePlanner}
            heading={`Move '${props.selectedCustomer.name}' to a new App Group`}
            primaryButtonProps={{disabled: !isMoveValid()}}
            onCancelButtonClick={() => {
                props.handleClose();
            }}
            onPrimaryButtonClick={() => handleMoveCustomer()}
            onClose={() => props.handleClose()}
        >
            <div style={{marginTop: '16px'}}>
                {renderModalContent()}
            </div>
        </Modal>
    </>

    return (
        <>
            {content}
        </>
    )

}

export default MoveCustomerToAppGroupModal;
